import { computed } from 'vue';
import { DEFAULT_EDVERUM_CLIENT, EDVERUM_CLIENTS } from '@/constants/edverumClient.js';
import { initialState } from '@/Helpers.js';

/**
 * Read the active Edverum client identifier from the initial state injected
 * by the backend (window.__INITIAL_STATE__.EnvironmentSettings.client).
 *
 * Falls back to {@link DEFAULT_EDVERUM_CLIENT} so callers always receive a
 * known value, mirroring how the existing wrapper components handle missing
 * state.
 *
 * @returns {string}
 */
export function getCurrentEdverumClient() {
    return initialState('EnvironmentSettings.client', DEFAULT_EDVERUM_CLIENT);
}

/**
 * @returns {boolean} True when the application is running in corporate mode.
 */
export function isCorporateClient() {
    return getCurrentEdverumClient() === EDVERUM_CLIENTS.CORPORATE;
}

/**
 * @returns {boolean} True when the application is running in stanbridge mode.
 */
export function isStanbridgeClient() {
    return getCurrentEdverumClient() === EDVERUM_CLIENTS.STANBRIDGE;
}

/**
 * Resolve a per-client component (or any value) from a map keyed by client
 * identifier. Replaces the binary `isStanbridge ? X : Y` ternary used by the
 * client-aware wrapper components, so adding a new EdverumClient enum case
 * surfaces a loud error instead of silently routing the new client variant into
 * whichever branch happened to be the ternary fallback.
 *
 * Resolution rules:
 *   1. If `map[currentClient]` exists, return it.
 *   2. Else, log a console.error describing the gap and fall back to
 *      `map[DEFAULT_EDVERUM_CLIENT]` so the UI degrades to the safe default
 *      instead of crashing in production.
 *   3. If even the default key isn't present in the map, throw — that's an
 *      unambiguous developer error and we want it surfaced immediately.
 *
 * @template T
 * @param {Record<string, T>} map
 * @returns {T}
 */
export function resolveClientComponent(map) {
    const client = getCurrentEdverumClient();

    if (Object.hasOwn(map, client)) {
        return map[client];
    }

    if (Object.hasOwn(map, DEFAULT_EDVERUM_CLIENT)) {
        console.error(
            `[useEdverumClient] No entry registered for edverum client "${client}". ` +
                `Registered keys: [${Object.keys(map).join(', ')}]. ` +
                `Falling back to "${DEFAULT_EDVERUM_CLIENT}".`
        );
        return map[DEFAULT_EDVERUM_CLIENT];
    }

    throw new Error(
        `[useEdverumClient] No entry registered for edverum client "${client}" ` +
            `and no fallback under "${DEFAULT_EDVERUM_CLIENT}". ` +
            `Registered keys: [${Object.keys(map).join(', ')}].`
    );
}

/**
 * Composable form of the Edverum client check. Use this inside Vue components
 * so the values stay reactive within the component lifecycle.
 *
 * For router modules, middleware, or other non-component code, prefer the
 * plain {@link isCorporateClient} / {@link isStanbridgeClient} helpers.
 *
 * @returns {{
 *   client: import('vue').ComputedRef<string>,
 *   isCorporate: import('vue').ComputedRef<boolean>,
 *   isStanbridge: import('vue').ComputedRef<boolean>,
 * }}
 */
export function useEdverumClient() {
    const client = computed(() => getCurrentEdverumClient());

    return {
        client,
        isCorporate: computed(() => isCorporateClient()),
        isStanbridge: computed(() => isStanbridgeClient()),
    };
}
